import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CdkDragDrop, CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { EditorDataService } from '../../../features/editor/services/editor-data.service';
import { EditorFilesService } from '../../../features/editor/services/editor-files.service';
import { EditorStateService } from '../../../features/editor/services/editor-state.service';
import { AudioRef, ChapterData, ProjectData, SceneRef, TimelineAudioBlock, TimelineSceneBlock } from '../../models';
import { cloneDeep } from '../../utils/clone';
import { attributesMatch } from '../../utils/attributes-match';
import { formatTimecode, resolvePlayhead } from '../../utils/frames';
import { planRawMove, rawInsertIndex } from './ref-index';

interface SceneJsonData {
	framesCount?: number;
	background?: { source?: { src?: string; dataType?: string } };
}

@Component({
	selector: 'app-timeline-panel',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, CdkDrag, CdkDropList, CdkScrollable],
	templateUrl: './timeline-panel.html',
	styleUrl: './timeline-panel.scss',
})
export class TimelinePanelComponent implements OnDestroy {
	// ── Fields: public (inputs/outputs) ─────────────────────────────────────
	projectData = input<ProjectData>();
	slug = input.required<string>();
	activeSceneSlug = input<string | null>(null);
	activeAudioSlug = input<string | null>(null);
	argsMap = input<Record<string, string | number | boolean>>({});

	currentTimeMs = input(0);
	/** Incremented by the editor when scene data changes (e.g. framesCount). Forces re-fetch. */
	refreshKey = input(0);

	projectDataChange = output<ProjectData>();
	sceneSelect = output<{ chapterSlug: string | null; sceneSlug: string }>();
	audioSelect = output<{ chapterSlug: string | null; audioSlug: string }>();
	seekTo = output<number>();

	// ── Fields: protected ───────────────────────────────────────────────────
	selectedChapterIndex = input(0);
	protected scenesLoading = signal(false);
	protected pixelsPerSecond = signal(60);

	// ── Fields: private (unfiltered data) ───────────────────────────────────
	private allSceneBlocks = signal<TimelineSceneBlock[]>([]);
	/** Audio refs paired with their position in the RAW `audioTrack` array. */
	private allAudioRefs = signal<{ ref: AudioRef; rawIndex: number }[]>([]);

	// ── Fields: private ─────────────────────────────────────────────────────
	private dataService = inject(EditorDataService);
	private filesService = inject(EditorFilesService);
	private stateService = inject(EditorStateService);
	private previousChapterKey = '';
	private previousStructuralKey = '';
	private versionRefetchTimer: ReturnType<typeof setTimeout> | null = null;

	/** True when the loaded project is imported — blocks drag/drop + removes. */
	protected readonly isReadOnly = this.stateService.isReadOnly;

	// ── Computed signals ────────────────────────────────────────────────────
	protected chapters = computed(() => {
		const data = this.projectData();
		return data?.chapters ?? [];
	});

	protected chapterOptions = computed(() =>
		this.chapters().map((ch, i) => ({ label: ch.slug, value: i }))
	);

	protected selectedChapter = computed(() => {
		const chs = this.chapters();
		const idx = this.selectedChapterIndex();
		return chs[idx] ?? null;
	});

	protected fps = computed(() => this.projectData()?.fps ?? 25);

	protected totalDurationSeconds = computed(() => {
		const blocks = this.sceneBlocks();
		if (!blocks.length) return 0;
		const last = blocks[blocks.length - 1];
		return last.startSeconds + last.durationSeconds;
	});

	protected sceneBlocks = computed(() => {
		const all = this.allSceneBlocks();
		const args = this.argsMap();
		const filtered = all.filter(b => this.matchesAttributes(b.attributes, args));
		let cumulative = 0;
		return filtered.map(b => {
			// Re-evaluate hasVersions reactively from the files signal
			const media = b.bgSrc ? this.filesService.findByBaseUrl(b.bgSrc) : null;
			const block: TimelineSceneBlock = { ...b, startSeconds: cumulative, hasVersions: media ? this.mediaHasVersions(media) : b.hasVersions };
			cumulative += b.durationSeconds;
			return block;
		});
	});

	protected audioBlocks = computed(() => {
		const audioRefs = this.allAudioRefs();
		const args = this.argsMap();
		const blocks = this.sceneBlocks();
		const fps = this.fps();

		return audioRefs
			.filter(({ ref }) => this.matchesAttributes(ref.attributes, args))
			.filter(({ ref }) => {
				if (!ref.start) return true;
				return blocks.some(b => b.slug === ref.start);
			})
			.flatMap<TimelineAudioBlock>(({ ref: audio, rawIndex }) => {
				// Duration always comes from the media pool (ffprobe on upload or
				// mediabunny during import). If the media has no duration we skip
				// the block entirely — rendering with a guessed duration would
				// mislead the user.
				const media = audio.src ? this.filesService.findByBaseUrl(audio.src) : null;
				if (!media?.durationSeconds) return [];

				const refScene = audio.start ? blocks.find(b => b.slug === audio.start) : null;
				const startSec = this.computeAudioStartSec(audio, refScene, fps);

				return [{
					refIndex: rawIndex,
					slug: audio.slug,
					startSeconds: startSec,
					durationSeconds: media.durationSeconds,
					disabled: audio.disabled,
					hasVersions: this.mediaHasVersions(media),
				}];
			});
	});

	/** External media (imported from CDN) always has all variants generated — treat as ready. */
	private mediaHasVersions(media: { isLocal?: boolean; versionCount?: number }): boolean {
		if (media.isLocal === false) return true;
		return (media.versionCount ?? 0) > 1;
	}

	/** Compute the audio block's start position on the timeline from its start/startFrame fields. */
	private computeAudioStartSec(
		audio: AudioRef,
		refScene: TimelineSceneBlock | null | undefined,
		fps: number,
	): number {
		const frameOffsetSec = audio.startFrame != null ? audio.startFrame / fps : 0;
		const sceneOffsetSec = refScene?.startSeconds ?? 0;
		return sceneOffsetSec + frameOffsetSec;
	}

	protected timelineWidthPx = computed(() =>
		Math.max(this.totalDurationSeconds() * this.pixelsPerSecond(), 200)
	);

	/** Override time during seeking drag — null means use player time */
	private seekOverrideMs = signal<number | null>(null);

	protected playheadPx = computed(() => {
		const timeMs = this.seekOverrideMs() ?? this.currentTimeMs();
		const timeS = timeMs / 1000;
		return 50 + timeS * this.pixelsPerSecond(); // 50px = row label width
	});

	/**
	 * Where the playhead is, in the units the data model uses (DEV-654).
	 *
	 * Every editable frame field — `ContentItem.startFrame`, `Placement.frames`,
	 * `OpacityKeyframe`, `SceneEffect.frameIndex` — is a **scene-local** index,
	 * so the scene frame is the operator-facing number. The chapter frame is the
	 * raw value the player itself holds and is kept because it costs nothing and
	 * is the one that can be checked against the player.
	 *
	 * Reads the same `seekOverrideMs ?? currentTimeMs` as `playheadPx()`, so the
	 * number and the line can never disagree — including mid-drag, where the
	 * player has not moved yet.
	 */
	protected playhead = computed(() =>
		resolvePlayhead(this.seekOverrideMs() ?? this.currentTimeMs(), this.fps(), this.sceneBlocks())
	);
	protected timecode = computed(() => formatTimecode(this.seekOverrideMs() ?? this.currentTimeMs()));

	protected rulerTicks = computed(() => {
		const pps = this.pixelsPerSecond();
		const total = this.totalDurationSeconds();
		if (total === 0) return [];

		let interval: number;
		if (pps >= 200) interval = 0.5;
		else if (pps >= 80) interval = 1;
		else if (pps >= 40) interval = 2;
		else interval = 5;

		const ticks: { seconds: number; px: number; label: string; major: boolean }[] = [];
		for (let s = 0; s <= total + interval; s += interval) {
			const rounded = Math.round(s * 100) / 100;
			ticks.push({
				seconds: rounded,
				px: rounded * pps,
				label: this.formatTime(rounded),
				major: rounded % (interval * 2) === 0 || interval >= 2,
			});
		}
		return ticks;
	});

	// ── Constructor (effects) ───────────────────────────────────────────────
	constructor() {
		effect(() => {
			const slug = this.slug();
			const fps = this.fps();
			if (!slug) {
				this.allSceneBlocks.set([]);
				return;
			}

			const chapter = this.selectedChapter();
			const hasChapters = this.chapters().length > 0;

			if (hasChapters && !chapter) {
				this.allSceneBlocks.set([]);
				return;
			}

			// Pair every ref with its RAW index before `alt` refs are dropped; the
			// blocks carry it through so a rendered position is never written back
			// to `scenes` as though the two lists lined up.
			const sceneRefs: { ref: SceneRef; rawIndex: number }[] = (hasChapters ? (chapter.scenes ?? []) : (this.projectData()?.scenes ?? []))
				.map((ref, rawIndex) => ({ ref, rawIndex }))
				.filter(({ ref }) => !ref.alt);
			const chapterKey = hasChapters ? chapter.slug : '__project__';
			const sceneKeys = sceneRefs.map(({ ref }) => ref.slug + (ref.externalChapter ?? '')).join(',');
			const refresh = this.refreshKey();
			const stateVersion = this.stateService.version();
			// Audio count intentionally NOT in this key — audio changes do not
			// require re-fetching scene JSONs and previously caused full
			// timeline rebuilds (and risked wiping all blocks via forkJoin
			// errors) on every audio add/remove. A separate effect below keeps
			// `allAudioRefs` in sync.
			const structuralKey = `${chapterKey}:${sceneKeys}:${refresh}`;
			const key = `${structuralKey}:${stateVersion}`;
			if (key === this.previousChapterKey) return;
			const structuralChange = structuralKey !== this.previousStructuralKey;
			this.previousChapterKey = key;
			this.previousStructuralKey = structuralKey;

			if (this.versionRefetchTimer !== null) {
				clearTimeout(this.versionRefetchTimer);
				this.versionRefetchTimer = null;
			}
			if (structuralChange) {
				// Real navigation (chapter switch, explicit refresh) — fetch
				// immediately, same as before.
				this.fetchSceneBlocks(slug, hasChapters ? chapter : null, sceneRefs, fps);
			} else {
				// `stateVersion` alone changed — a scene/image form field edit
				// bumps this on every keystroke (setScene() in scene-form's
				// patch()). Debounce so typing doesn't HTTP-refetch scene blocks
				// per keystroke.
				this.versionRefetchTimer = setTimeout(() => {
					this.versionRefetchTimer = null;
					this.fetchSceneBlocks(slug, hasChapters ? chapter : null, sceneRefs, fps);
				}, 500);
			}
		});

		// Keep audio refs in sync independently of scene fetches.
		effect(() => {
			const data = this.projectData();
			if (!data) {
				this.allAudioRefs.set([]);
				return;
			}
			const hasChapters = (data.chapters?.length ?? 0) > 0;
			const chapter = hasChapters ? data.chapters?.[this.selectedChapterIndex()] ?? null : null;
			this.storeAudioRefs(chapter);
		});
	}

	// ── Seek ────────────────────────────────────────────────────────────────
	private seekTracksEl: HTMLElement | null = null;

	protected onRulerMouseDown(event: MouseEvent): void {
		event.preventDefault();
		const target = event.target as HTMLElement;
		this.seekTracksEl = target.closest('.timeline-tracks') as HTMLElement | null;
		if (!this.seekTracksEl) return;
		this.seekOverrideMs.set(this.timeFromX(event.clientX));
		document.addEventListener('mousemove', this.onSeekMouseMove);
		document.addEventListener('mouseup', this.onSeekMouseUp);
	}

	private onSeekMouseMove = (event: MouseEvent): void => {
		this.seekOverrideMs.set(this.timeFromX(event.clientX));
	};

	private onSeekMouseUp = (): void => {
		const timeMs = this.seekOverrideMs();
		this.seekOverrideMs.set(null);
		this.seekTracksEl = null;
		document.removeEventListener('mousemove', this.onSeekMouseMove);
		document.removeEventListener('mouseup', this.onSeekMouseUp);
		if (timeMs != null) {
			this.seekTo.emit(timeMs);
		}
	};

	private timeFromX(clientX: number): number {
		if (!this.seekTracksEl) return 0;
		const rect = this.seekTracksEl.getBoundingClientRect();
		const x = clientX - rect.left + this.seekTracksEl.scrollLeft - 50;
		const timeS = Math.max(0, Math.min(x / this.pixelsPerSecond(), this.totalDurationSeconds()));
		return Math.round(timeS * 1000);
	}

	// ── Methods: protected ──────────────────────────────────────────────────
	protected zoomIn(): void {
		this.pixelsPerSecond.update(v => Math.min(500, v + 20));
	}

	protected zoomOut(): void {
		this.pixelsPerSecond.update(v => Math.max(20, v - 20));
	}

	// ── Native drag-and-drop from file browser ─────────────────────────────
	protected videoDragOver = signal(false);
	protected audioDragOver = signal(false);
	protected dropInsertIndex = signal<number | null>(null);

	fileDrop = output<{ type: 'video' | 'audio'; baseUrl: string; name: string; durationSeconds?: number; fps?: number; insertIndex?: number; dataType?: 'VIDEO' | 'IMAGE' }>();

	private parseDropData(event: DragEvent): { baseUrl: string; name: string; category: string; durationSeconds?: number; fps?: number } | null {
		const raw = event.dataTransfer?.getData('application/webout-file');
		if (!raw) return null;
		try { return JSON.parse(raw); } catch { return null; }
	}

	private hasWeboutFile(event: DragEvent): boolean {
		return Array.from(event.dataTransfer?.types ?? []).includes('application/webout-file');
	}

	/** Length of the RAW ref array the editor will splice — `alt` refs included. */
	private rawSceneRefCount(): number {
		const data = this.projectData();
		if (!data) return 0;
		const chapter = this.chapters().length ? data.chapters?.[this.selectedChapterIndex()] : null;
		return (this.chapters().length ? chapter?.scenes : data.scenes)?.length ?? 0;
	}

	private calcInsertIndex(event: DragEvent): number {
		const row = (event.currentTarget as HTMLElement);
		const rect = row.getBoundingClientRect();
		const x = event.clientX - rect.left + row.scrollLeft;
		const blocks = this.sceneBlocks();
		const pps = this.pixelsPerSecond();
		let cumPx = 0;
		for (let i = 0; i < blocks.length; i++) {
			const blockW = blocks[i].durationSeconds * pps;
			if (x < cumPx + blockW / 2) return i;
			cumPx += blockW;
		}
		return blocks.length;
	}

	protected insertIndicatorPx = computed(() => {
		const idx = this.dropInsertIndex();
		if (idx === null) return null;
		const blocks = this.sceneBlocks();
		const pps = this.pixelsPerSecond();
		let px = 0;
		for (let i = 0; i < idx && i < blocks.length; i++) {
			px += blocks[i].durationSeconds * pps;
		}
		return px;
	});

	protected onVideoDragOver(event: DragEvent): void {
		if (this.isReadOnly()) return;
		if (!this.hasWeboutFile(event)) return;
		event.preventDefault();
		if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
		this.videoDragOver.set(true);
		this.dropInsertIndex.set(this.calcInsertIndex(event));
	}

	protected onVideoDragLeave(): void {
		this.videoDragOver.set(false);
		this.dropInsertIndex.set(null);
	}

	protected onVideoDrop(event: DragEvent): void {
		if (this.isReadOnly()) return;
		event.preventDefault();
		const insertIndex = this.dropInsertIndex();
		this.videoDragOver.set(false);
		this.dropInsertIndex.set(null);
		const file = this.parseDropData(event);
		if (!file || (file.category !== 'video' && file.category !== 'images')) return;
		this.fileDrop.emit({
			type: 'video',
			baseUrl: file.baseUrl,
			name: file.name,
			durationSeconds: file.durationSeconds,
			fps: file.fps,
			// `calcInsertIndex` works in rendered positions (it has to — it measures
			// pixels against the blocks on screen). The editor splices the RAW
			// `scenes` array, so the position is translated here, once.
			insertIndex: insertIndex == null ? undefined : rawInsertIndex(this.sceneBlocks(), insertIndex, this.rawSceneRefCount()),
			dataType: file.category === 'images' ? 'IMAGE' : 'VIDEO',
		});
	}

	protected onAudioDragOver(event: DragEvent): void {
		if (this.isReadOnly()) return;
		if (!this.hasWeboutFile(event)) return;
		event.preventDefault();
		if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
		this.audioDragOver.set(true);
	}

	protected onAudioDragLeave(): void {
		this.audioDragOver.set(false);
	}

	protected onAudioDrop(event: DragEvent): void {
		if (this.isReadOnly()) return;
		event.preventDefault();
		this.audioDragOver.set(false);
		const file = this.parseDropData(event);
		if (!file || file.category !== 'audios') return;
		this.fileDrop.emit({ type: 'audio', baseUrl: file.baseUrl, name: file.name, durationSeconds: file.durationSeconds, fps: file.fps });
	}

	// ── Scene remove from timeline ──────────────────────────────────────────
	sceneRemove = output<{ chapterSlug: string | null; sceneSlug: string }>();
	audioRemove = output<{ index: number }>();

	protected removeScene(block: TimelineSceneBlock, event: MouseEvent): void {
		if (this.isReadOnly()) return;
		event.stopPropagation();
		if (!confirm(`Remove scene "${block.slug}"?`)) return;
		const chapter = this.selectedChapter();
		this.sceneRemove.emit({ chapterSlug: chapter?.slug ?? null, sceneSlug: block.splitParent ?? block.slug });
	}

	protected removeAudio(block: TimelineAudioBlock, event: MouseEvent): void {
		if (this.isReadOnly()) return;
		event.stopPropagation();
		if (!confirm('Remove this audio track?')) return;
		// The RAW index, never the rendered one — the editor splices `audioTrack`
		// with it, and the rendered position addresses a different track whenever
		// anything ahead of this block was filtered out.
		this.audioRemove.emit({ index: block.refIndex });
	}

	protected dropScene(event: CdkDragDrop<TimelineSceneBlock[]>): void {
		if (this.isReadOnly()) return;
		if (event.previousIndex === event.currentIndex) return;

		const blocks = this.sceneBlocks();
		const draggedBlock = blocks[event.previousIndex];

		// Don't allow reordering individual split parts
		if (draggedBlock.splitParent) return;

		const data = this.projectData();
		if (!data) return;

		const clone = cloneDeep(data);
		const hasChapters = this.chapters().length > 0;

		let scenesArray: SceneRef[];
		if (hasChapters) {
			const chapter = clone.chapters?.[this.selectedChapterIndex()];
			if (!chapter?.scenes) return;
			scenesArray = chapter.scenes;
		} else {
			if (!clone.scenes) return;
			scenesArray = clone.scenes;
		}

		// `blocks` is the FILTERED list, so its positions are not indices into
		// `scenesArray`. Every block carries the raw index instead; `planRawMove`
		// is the one place the two coordinate spaces meet.
		const plan = planRawMove(blocks, draggedBlock.refIndex, event.currentIndex, scenesArray.length);
		if (plan.removeAt === plan.insertAt) return;

		const [movedRef] = scenesArray.splice(plan.removeAt, 1);
		scenesArray.splice(plan.insertAt, 0, movedRef);

		// Optimistically reorder local blocks so the timeline stays visible while
		// the effect re-fetches. Re-derive the order from the mutated ref array by
		// IDENTITY rather than by index — `allSceneBlocks` excludes `alt` refs, so
		// raw indices do not address it either.
		const groups = new Map<string, TimelineSceneBlock[]>();
		for (const b of this.allSceneBlocks()) {
			const key = b.splitParent ?? b.slug;
			const group = groups.get(key);
			if (group) group.push(b);
			else groups.set(key, [b]);
		}
		let cumulative = 0;
		const reordered = scenesArray
			.flatMap(ref => groups.get(ref.slug) ?? [])
			.map(b => {
				const block = { ...b, startSeconds: cumulative };
				cumulative += b.durationSeconds;
				return block;
			});
		this.allSceneBlocks.set(reordered);

		this.projectDataChange.emit(clone);
	}

	protected clickScene(block: TimelineSceneBlock): void {
		if (block.externalChapter) {
			const slash = block.externalChapter.indexOf('/');
			const baseSlug = block.externalChapter.substring(slash + 1);
			this.sceneSelect.emit({
				chapterSlug: block.externalChapter.substring(0, slash),
				sceneSlug: block.splitParent ? `${baseSlug}__${block.splitIndex}` : baseSlug,
			});
			return;
		}

		const chapter = this.selectedChapter();
		this.sceneSelect.emit({
			chapterSlug: chapter?.slug ?? null,
			sceneSlug: block.slug,
		});
	}

	protected clickAudio(block: TimelineAudioBlock): void {
		const chapter = this.selectedChapter();
		this.audioSelect.emit({
			chapterSlug: chapter?.slug ?? null,
			audioSlug: block.slug,
		});
	}

	// ── Methods: private ────────────────────────────────────────────────────
	private fetchSceneBlocks(projectSlug: string, chapter: ChapterData | null, sceneRefs: { ref: SceneRef; rawIndex: number }[], fps: number): void {
		if (!sceneRefs.length) {
			this.allSceneBlocks.set([]);
			this.storeAudioRefs(chapter);
			return;
		}

		this.scenesLoading.set(true);

		const chapterScenePath = (ch: string, sc: string) =>
			`/projects/${projectSlug}/chapters/${ch}/scenes/${sc}/scene.json`;
		const projectScenePath = (sc: string) =>
			`/projects/${projectSlug}/scenes/${sc}/scene.json`;

		const resolvePath = (ref: SceneRef): ((sc: string) => string) => {
			if (ref.externalChapter) {
				const slash = ref.externalChapter.indexOf('/');
				const chSlug = ref.externalChapter.substring(0, slash);
				return (sc) => chapterScenePath(chSlug, sc);
			} else if (chapter) {
				return (sc) => chapterScenePath(chapter.slug, sc);
			}
			return projectScenePath;
		};

		// Try to resolve from state service cache first, fall back to HTTP.
		// Each HTTP request is wrapped in catchError so a single 404 (e.g. a
		// just-added scene whose PUT has not yet committed on the backend)
		// degrades to an empty placeholder block instead of rejecting the
		// whole forkJoin and wiping every block off the timeline.
		const safeLoad = (path: string): Observable<SceneJsonData> => {
			const cached = this.stateService.peekScene(path);
			if (cached) return of(cached);
			return this.dataService.loadFile<SceneJsonData>(path).pipe(
				catchError(() => of({} as SceneJsonData))
			);
		};
		const requests: Observable<SceneJsonData | SceneJsonData[]>[] = sceneRefs.map(({ ref }) => {
			const scSlug = ref.externalChapter
				? ref.externalChapter.substring(ref.externalChapter.indexOf('/') + 1)
				: ref.slug;
			const scenePath = resolvePath(ref);

			const splitCount = ref.split && ref.split > 1 ? ref.split : 1;
			if (splitCount > 1) {
				const splitRequests = Array.from({ length: splitCount }, (_, i) =>
					safeLoad(scenePath(`${scSlug}__${i}`))
				);
				return forkJoin(splitRequests);
			}
			return safeLoad(scenePath(scSlug));
		});

		forkJoin(requests).subscribe({
			next: (results) => {
				let cumulative = 0;
				const blocks: TimelineSceneBlock[] = [];
				sceneRefs.forEach(({ ref, rawIndex }, i) => {
					const result = results[i];
					if (Array.isArray(result)) {
						result.forEach((part, partIndex) => {
							const framesCount = part.framesCount ?? 0;
							const duration = framesCount / fps;
							const bgSrc = part.background?.source?.src;
							const media = bgSrc ? this.filesService.findByBaseUrl(bgSrc) : null;
							blocks.push({
								refIndex: rawIndex,
								slug: `${ref.slug}__${partIndex}`,
								framesCount,
								durationSeconds: duration,
								startSeconds: cumulative,
								disabled: ref.disabled,
								split: ref.split,
								splitParent: ref.slug,
								splitIndex: partIndex,
								externalChapter: ref.externalChapter,
								attributes: ref.attributes,
								hasVersions: media ? this.mediaHasVersions(media) : undefined,
								bgSrc,
								isImage: part.background?.source?.dataType === 'IMAGE',
							});
							cumulative += duration;
						});
					} else {
						const framesCount = result.framesCount ?? 0;
						const duration = framesCount / fps;
						const bgSrc = result.background?.source?.src;
						const media = bgSrc ? this.filesService.findByBaseUrl(bgSrc) : null;
						blocks.push({
							refIndex: rawIndex,
							slug: ref.slug,
							framesCount,
							durationSeconds: duration,
							startSeconds: cumulative,
							disabled: ref.disabled,
							externalChapter: ref.externalChapter,
							attributes: ref.attributes,
							hasVersions: media ? this.mediaHasVersions(media) : undefined,
							bgSrc,
							isImage: result.background?.source?.dataType === 'IMAGE',
						});
						cumulative += duration;
					}
				});
				this.allSceneBlocks.set(blocks);
				this.storeAudioRefs(chapter);
				this.scenesLoading.set(false);
			},
			error: () => {
				// Per-request errors are swallowed by `safeLoad` above, so this
				// branch only fires on truly unexpected failures (e.g. an
				// uncaught exception in the next handler). Stop the spinner
				// without clobbering the previously-rendered blocks — wiping
				// them is what produced the original "timeline goes blank
				// after adding video/audio" bug.
				this.scenesLoading.set(false);
			},
		});
	}

	private storeAudioRefs(chapter: ChapterData | null): void {
		const data = this.projectData();
		const audioSource: { audioTrack?: AudioRef[] } | undefined = chapter ?? data;
		if (!audioSource) {
			this.allAudioRefs.set([]);
			return;
		}
		// Pair every ref with its RAW index before `alt` refs are dropped —
		// losing it here is what let a later filter's position be written back
		// to `audioTrack` as if it addressed the same element.
		this.allAudioRefs.set(
			(audioSource.audioTrack ?? [])
				.map((ref, rawIndex) => ({ ref, rawIndex }))
				.filter(({ ref }) => !ref.alt),
		);
	}

	/**
	 * Shared with the chapter selector and the graph so the three cannot drift
	 * (DEV-749 made this strict; DEV-833 gave chapters the same treatment).
	 */
	private matchesAttributes(
		attributes: Record<string, unknown> | undefined,
		argsMap: Record<string, string | number | boolean>,
	): boolean {
		return attributesMatch(attributes, argsMap);
	}

	private formatTime(seconds: number): string {
		if (seconds < 60) {
			return seconds % 1 === 0 ? `${seconds}s` : `${seconds.toFixed(1)}s`;
		}
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return s === 0 ? `${m}m` : `${m}m${s.toFixed(0)}s`;
	}

	ngOnDestroy(): void {
		if (this.versionRefetchTimer !== null) clearTimeout(this.versionRefetchTimer);
	}
}
